<template>
    <div>
        <TabGroup
            v-slot="{ selectedIndex }"
            :selected-index="controlledIndex"
            @change="controlledIndex = $event"
        >
            <!--
                The bottom padding is the gap between the bar and the panels it
                labels, so `barOnly` drops it: with no panels it becomes dead space
                between the bar's own border and whatever the caller put next,
                which the caller cannot remove from the outside.
            -->
            <div
                class="h-auto max-md:overflow-x-scroll"
                :class="barOnly ? '' : 'pb-4'"
            >
                <TabList class="border-b max-md:flex md:relative md:flex">
                    <Tab
                        v-for="(option, index) in tabs"
                        :key="index"
                        :ref="
                            (el) => {
                                if (el) tabRefs[index] = el.$el;
                            }
                        "
                        v-slot="{ selected }"
                        :disabled="option.disabled === true"
                        class="flex items-center justify-center whitespace-nowrap px-1 py-3 text-sm transition-colors focus-visible:outline-none disabled:cursor-not-allowed max-md:relative max-md:ml-3 max-md:!w-auto max-md:!flex-none first:max-md:ml-0 md:px-4 md:text-base"
                        :class="noStretch ? 'flex-none' : 'flex-1'"
                        :title="option.tooltip ?? option.name"
                    >
                        <span
                            :class="[
                                option.disabled === true ? 'text-muval-gray-4'
                                : selected ? 'font-medium text-brand'
                                : 'text-muval-gray-2',
                                size === 'lg' ? 'text-base' : 'text-sm',
                            ]"
                            class="transition"
                        >
                            <span>{{ option.name }}</span>
                        </span>
                        <span
                            v-if="Number.isInteger(option.count)"
                            class="ml-2 rounded px-1 py-px text-xs font-medium leading-tight text-white transition-colors"
                            :class="getBadgeClasses(option, selected)"
                            >{{ option.count }}</span
                        >

                        <div
                            class="absolute -bottom-[1px] left-0 hidden h-1 w-full rounded-full bg-brand transition duration-200"
                            :class="{ 'max-md:!block': selected }"
                        />
                    </Tab>
                    <div
                        class="absolute -bottom-[2px] left-0 hidden h-1 rounded-full bg-brand transition duration-200 md:block"
                        :style="
                            indicatorAutoWidth ? indicatorStyle : (
                                `width: ${100 / tabs.length}%; transform: translateX(${100 * selectedIndex}%)`
                            )
                        "
                    />
                </TabList>
            </div>
            <!--
                `barOnly` is for callers that render their own content, usually
                because the bar and the panels have to live in different slots of a
                surrounding component (a modal that pins its tabs above a scrolling
                body). Without it those callers still get one empty TabPanel per
                tab, which is harmless but pure noise in the DOM.
            -->
            <TabPanels v-if="!barOnly">
                <TabPanel
                    v-for="(option, index) in tabs"
                    :key="index"
                    :unmount="unmount"
                >
                    <slot
                        name="tabPanel"
                        v-bind="option"
                    >
                        <component
                            :is="option.component"
                            v-bind="option?.props ?? {}"
                        />
                    </slot>
                </TabPanel>
            </TabPanels>
        </TabGroup>
    </div>
</template>
<script setup>
import { nextTick, onBeforeUnmount } from 'vue';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';

const { tabs, unmount, activeTabName, activeTabIndex, indicatorAutoWidth, indicatorDefaultWidth } = defineProps({
    /**
     * `{ name, count?, tooltip?, badgeColor?, disabled?, component?, props? }`.
     *
     * A `disabled` tab is greyed and unselectable - headlessui also skips it in
     * keyboard navigation. Use it rather than dropping the tab when the tab's
     * absence would be the more confusing state (a feature that exists but is not
     * available yet), and drop the tab when it will never apply.
     */
    tabs: {
        type: Array,
        required: true,
    },
    unmount: {
        type: Boolean,
        default: false,
    },
    /**
     * Render the tab bar without its panels, for callers that own their own
     * content - see the comment on `TabPanels` below.
     */
    barOnly: {
        type: Boolean,
        default: false,
    },
    /**
     * Size tabs to their labels instead of stretching them to fill the bar.
     * Equal-width reads well across two or three tabs; past that it strands the
     * labels in wide empty cells. Pair with `indicatorAutoWidth`, since the
     * indicator's default maths assumes every tab is the same width.
     */
    noStretch: {
        type: Boolean,
        default: false,
    },
    activeTabName: {
        type: String,
        default: null,
    },
    activeTabIndex: {
        type: Number,
        default: null,
    },
    size: {
        type: String,
        default: 'lg',
    },
    indicatorAutoWidth: {
        type: Boolean,
        default: false,
    },
    indicatorDefaultWidth: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['change']);

const controlledIndex = ref(0);
const tabRefs = ref([]);
const indicatorStyle = ref({});

function getBadgeClasses(option, selected) {
    if (option.badgeColor === 'red') {
        // For red badges (Issues tab), if count > 0, always show red (don't grey out)
        if (option.count > 0) {
            return selected ? 'bg-muval-red-1' : 'bg-muval-red-1';
        }
        // If count is 0, use normal blue badge behavior
        return selected ? 'bg-brand' : 'bg-muval-gray-3';
    }
    // Default blue badge
    return selected ? 'bg-brand' : 'bg-muval-gray-3';
}

function resolveTabName(option) {
    if (!option) return null;
    const nameCandidate = option.name;
    if (typeof nameCandidate === 'string') return nameCandidate;
    if (nameCandidate && typeof nameCandidate === 'object' && 'value' in nameCandidate) {
        return nameCandidate.value;
    }
    return null;
}

// Special function to update the tab indicator width and position.
//
// Needed because the parent container of MuvalTabs can be narrow even on desktop.
// Using the default `100 / tabs.length` calculation does not match the actual tab width,
// which causes the indicator to misalign and break the UI.
//
// This function measures the **actual width and left offset of the active tab**
// so the indicator adapts to the real tab size. It also includes a fallback
// default width for the first render when the tab width isn't measurable yet (offsetWidth === 0).
//
// This is particularly used in the Diary view availability panels, where
// the tab container width can vary and misalignment would break the UI.
async function updateIndicatorWidth() {
    if (!indicatorAutoWidth) return;

    await nextTick();
    const el = tabRefs.value[controlledIndex.value];
    if (!el) return;

    // Fallback for the first render
    if (el.offsetWidth === 0 && indicatorDefaultWidth != '') {
        indicatorStyle.value = {
            width: indicatorDefaultWidth,
            transform: `translateX(0px)`,
        };
    } else {
        indicatorStyle.value = {
            width: `${el.offsetWidth}px`,
            transform: `translateX(${el.offsetLeft}px)`,
        };
    }
}

function applyInitialActiveTab() {
    if (typeof activeTabIndex === 'number' && activeTabIndex !== null) {
        controlledIndex.value = Math.max(0, Math.min(activeTabIndex, tabs.length - 1));
        return;
    }

    if (activeTabName && typeof activeTabName === 'string') {
        const idx = tabs.findIndex((t) => {
            const tName = resolveTabName(t);
            return tName && tName.toLowerCase() === activeTabName.toLowerCase();
        });
        if (idx >= 0) {
            controlledIndex.value = idx;
        }
    }
}

onMounted(() => {
    applyInitialActiveTab();
    updateIndicatorWidth();
    window.addEventListener('resize', updateIndicatorWidth);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateIndicatorWidth);
});

watch(
    () => activeTabName,
    () => {
        applyInitialActiveTab();
    },
);

watch(
    () => activeTabIndex,
    () => {
        applyInitialActiveTab();
    },
);

watch(controlledIndex, (newIndex) => {
    emit('change', newIndex);
    updateIndicatorWidth();
});

watch(
    () => tabs,
    () => {
        updateIndicatorWidth();
    },
);
</script>
